import React, {useState} from 'react';
import cx from 'classnames';
import {
    Flex,
    HTMLDialog,
    Button,
    Text,
    Input,
    ToggleSwitch
} from '@priority-software/priority-style-react';
import {DialogToolbarContent} from '../../../../../components/Dialog/DialogToolbar/DialogToolbar';
import {macroDialogID} from '../../../constants';
import {texts} from '../../../texts';
import styles from './style.module.scss';
import {formsStore} from '../../../../../stores/Form';

type Props = {
    onClose: () => void;
};

export const MacroModal = (props: Props) => {
    const {onClose} = props;
    const {setRecordingMacro, setIsRecording, setShowMacroButtons} = formsStore;

    const [macroName, setMacroName] = useState('');
    const [shortcut, setShortcut] = useState('');
    const [repeatable, setRepeatable] = useState(false);

    const startRecording = () => {
        onClose();

        setShowMacroButtons(true);
        setIsRecording(true);
        setRecordingMacro({
            title: macroName,
            shortCode: shortcut,
            isRecursive: repeatable
        });
    };

    return (
        <HTMLDialog
            isOpen
            id={macroDialogID}
            size="large"
            width={520}
            toolbarContent={
                <DialogToolbarContent
                    mainTitle={texts.dlg_macroSettings}
                    tooltipPortalID={macroDialogID}
                />
            }
            content={
                <MacroDialogContent
                    macroName={macroName}
                    shortcut={shortcut}
                    repeatable={repeatable}
                    handleShortcut={setShortcut}
                    handleRepeatable={setRepeatable}
                    handleName={setMacroName}
                />
            }
            footer={
                <Flex justifyContent="flex-end">
                    <Button
                        disabled={!macroName}
                        size="medium"
                        skin="primary"
                        onClick={startRecording}
                    >
                        {texts.macro_dialog_start_recording_btn}
                    </Button>
                </Flex>
            }
            onClose={onClose}
        />
    );
};

type ContentProps = {
    macroName: string;
    shortcut: string;
    repeatable: boolean;
    handleRepeatable: (val: boolean) => void;
    handleShortcut: (val: string) => void;
    handleName: (val: string) => void;
};

export const MacroDialogContent = (props: ContentProps) => {
    const {handleName, handleShortcut, handleRepeatable, macroName, shortcut, repeatable} = props;

    return (
        <div className={cx('reactComp', styles.content)}>
            <Text skin="paragraph-1"> {texts.macro_sub_title}</Text>
            <Flex justifyContent="flex-start" componentClasses={styles.controlGroup}>
                <div>
                    <Text tag="span" textColor="primary-700" skin="paragraph-1">
                        {texts.macro_dialog_name_label}
                    </Text>

                    <Input
                        autoFocus
                        componentClasses={styles.nameInput}
                        type="text"
                        size="large"
                        value={macroName}
                        onChange={e => handleName(e.target.value)}
                    />
                </div>

                <div>
                    <Text tag="span" textColor="primary-700" skin="paragraph-1">
                        {texts.macro_dialog_shortcut_label}
                    </Text>

                    <Flex alignItems="center" justifyContent="flex-start">
                        <Text
                            componentClasses={styles.altLabel}
                            tag="span"
                            textColor="primary-700"
                            skin="paragraph-1"
                        >
                            ALT +
                        </Text>

                        <Input
                            componentClasses={styles.shortcutInput}
                            type="text"
                            value={shortcut}
                            size="large"
                            onChange={e => handleShortcut(e.target.value)}
                        />
                    </Flex>
                </div>
            </Flex>

            <Flex alignItems="center" justifyContent="flex-start">
                <Text
                    tag="span"
                    textColor="primary-700"
                    skin="paragraph-1"
                    componentClasses={styles.repeatableLabel}
                >
                    {texts.macro_dialog_repeat_label}
                </Text>

                <ToggleSwitch onChange={handleRepeatable} checked={repeatable} />
            </Flex>
        </div>
    );
};
